import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DealCard from './DealCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealsHome = () => {
  const [hot, setHot] = useState([]);
  const [daily, setDaily] = useState([]);
  const [meta, setMeta] = useState({ categories: [], stores: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [h, d, m] = await Promise.all([
          axios.get(`${API}/deals/hot?limit=12`),
          axios.get(`${API}/deals/daily?limit=5`),
          axios.get(`${API}/deals/meta`),
        ]);
        setHot(h.data.deals || []);
        setDaily(d.data.deals || []);
        setMeta(m.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-semibold mb-4">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Fresh deals updated every 6 hours
            </div>
            <h1 data-testid="home-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Stop Overpaying Online.<br/>
              <span className="text-amber-400">DealPing</span> hunts the best prices so you don't have to.
            </h1>
            <p className="mt-5 text-lg text-zinc-300 max-w-2xl">
              Hand-picked hot deals from Amazon, Walmart, Target, Best Buy, Costco and more —
              categorized, scored, and delivered straight to your inbox.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/subscribe"
                data-testid="hero-subscribe-btn"
                className="inline-flex items-center justify-center rounded-md bg-amber-400 hover:bg-amber-300 text-black font-semibold px-6 py-3 text-sm"
              >
                Get free deal alerts
              </Link>
              <Link
                to="/deals"
                data-testid="hero-browse-btn"
                className="inline-flex items-center justify-center rounded-md border border-zinc-700 hover:border-amber-400 text-white px-6 py-3 text-sm"
              >
                Browse all deals
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DealPing Daily Drop */}
      {daily.length > 0 && (
        <section className="border-b border-zinc-900 bg-zinc-950/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-semibold mb-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  DealPing Daily Drop
                </div>
                <h2 data-testid="daily-drop-title" className="text-2xl font-bold">
                  Today's hand-picked {daily.length} deals
                </h2>
                <p className="text-sm text-zinc-400 mt-1">Curated every morning by our scoring engine.</p>
              </div>
            </div>
            <div data-testid="daily-drop-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {daily.map(d => <DealCard key={d.id} deal={d} />)}
            </div>
          </div>
        </section>
      )}

      {/* Hot deals */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 data-testid="hot-deals-title" className="text-2xl font-bold">Today's hottest deals</h2>
            <p className="text-sm text-zinc-400 mt-1">Scored by discount, popularity, and price range.</p>
          </div>
          <Link to="/deals" className="text-sm text-amber-400 hover:underline">See all →</Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse h-80" />
            ))}
          </div>
        ) : hot.length === 0 ? (
          <div data-testid="no-deals-msg" className="text-zinc-400 py-16 text-center">
            No hot deals right now — check back soon.
          </div>
        ) : (
          <div data-testid="hot-deals-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {hot.map(d => <DealCard key={d.id} deal={d} />)}
          </div>
        )}
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-zinc-900">
        <h2 className="text-2xl font-bold mb-6">Shop by category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {meta.categories.map(c => (
            <Link
              key={c.slug}
              to={`/deals/category/${c.slug}`}
              data-testid={`cat-tile-${c.slug}`}
              className="group p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-amber-400/60 hover:bg-zinc-900/60 transition"
            >
              <div className="text-sm font-semibold text-white group-hover:text-amber-400">{c.name}</div>
              <div className="text-xs text-zinc-500 mt-1">{c.count} deal{c.count === 1 ? '' : 's'}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Stores */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-zinc-900">
        <h2 className="text-2xl font-bold mb-6">Top stores</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {meta.stores.filter(s => s.slug !== 'other').map(s => (
            <Link
              key={s.slug}
              to={`/deals/store/${s.slug}`}
              data-testid={`store-tile-${s.slug}`}
              className="group p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-amber-400/60 transition"
            >
              <div className="text-sm font-semibold text-white group-hover:text-amber-400">{s.name}</div>
              <div className="text-xs text-zinc-500 mt-1">{s.count} deal{s.count === 1 ? '' : 's'}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DealsHome;
